import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadUser = () => {
      console.log('🔐 AuthContext: Loading user on app start');
      console.log('🔐 Current localStorage:', {
        token: !!localStorage.getItem('userToken'),
        userData: !!localStorage.getItem('userData'),
        userId: !!localStorage.getItem('userId')
      });
      
      const isAuthenticated = authService.isAuthenticated();
      console.log('🔐 Is authenticated:', isAuthenticated);
      
      if (isAuthenticated) {
        const userData = authService.getUser();
        console.log('🔐 User data loaded:', userData);
        setUser(userData);
      } else {
        console.log('🔐 No authentication found - clearing any partial data');
        // Clear any partial auth data
        localStorage.removeItem('userToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userId');
        setUser(null);
      }
      setLoading(false);
    };
    
    // Detect mobile Safari and add appropriate delay
    const isMobileSafari = /iPhone|iPad|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const delay = isMobileSafari ? 300 : 100; // Longer delay for mobile Safari
    
    console.log('🔐 AuthContext: Setting up with delay:', delay, 'isMobileSafari:', isMobileSafari);
    setTimeout(loadUser, delay);
  }, []);

  const login = async (credentials) => {
    try {
      setError(null);
      console.log('AuthContext login called with:', credentials);
      
      // Demo mode bypass for development
      if (process.env.REACT_APP_DEMO_MODE === 'true') {
        console.log('🎭 DEMO MODE: Bypassing authentication');
        const demoUser = {
          _id: 'demo-user-id',
          username: credentials.username || credentials.email || 'demo-user',
          email: credentials.email || 'demo@example.com',
          firstName: 'Demo',
          lastName: 'User',
          isDemoUser: true
        };
        
        // Set demo user data
        localStorage.setItem('userToken', 'demo-token-' + Date.now());
        localStorage.setItem('userId', demoUser._id);
        localStorage.setItem('userData', JSON.stringify(demoUser));
        setUser(demoUser);
        
        return { success: true, user: demoUser };
      }
      
      // Clear any previous user's data before logging in new user
      clearAllUserData();
      
      const response = await authService.login(credentials);
      console.log('AuthService response:', response);
      
      if (response && response.user) {
        setUser(response.user);
        console.log('User set in context:', response.user);
        return { success: true };
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle structured errors from AuthService
      const errorMessage = error.userMessage || error.message || 'Login failed';
      const errorType = error.type || 'GENERIC_ERROR';
      
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage,
        errorType: errorType,
        originalError: error.message
      };
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      console.log('AuthContext register called with:', JSON.stringify(userData, null, 2));
      
      // Demo mode bypass for development
      if (process.env.REACT_APP_DEMO_MODE === 'true') {
        console.log('🎭 DEMO MODE: Bypassing registration');
        const demoUser = {
          _id: 'demo-user-' + Date.now(),
          username: userData.username,
          email: userData.email,
          firstName: userData.firstName || 'Demo',
          lastName: userData.lastName || 'User',
          isDemoUser: true
        };
        
        // Set demo user data and auto-login
        localStorage.setItem('userToken', 'demo-token-' + Date.now());
        localStorage.setItem('userId', demoUser._id);
        localStorage.setItem('userData', JSON.stringify(demoUser));
        setUser(demoUser);
        
        return { success: true, user: demoUser };
      }
      
      const response = await authService.register(userData);
      console.log('AuthService register response:', JSON.stringify(response, null, 2));
      
      if (response.success || response.data || response.user) {
        // Try to login after successful registration
        await login({ username: userData.username, password: userData.password });
        return { success: true };
      }
      
      return { success: true }; // Assume success if no explicit failure
    } catch (error) {
      console.error('AuthContext register error details:');
      console.error('- Error object:', error);
      console.error('- Error message:', error.message);
      console.error('- Error type:', error.type);
      console.error('- User message:', error.userMessage);
      console.error('- Error response status:', error.response?.status);
      console.error('- Error response data:', error.response?.data);
      
      // Handle structured errors from AuthService
      const errorMessage = error.userMessage || error.message || 'Registration failed';
      const errorType = error.type || 'GENERIC_ERROR';
      
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage,
        errorType: errorType,
        originalError: error.message
      };
    }
  };

  const logout = () => {
    console.log('🚪 Logout called from AuthContext');
    console.trace('Logout stack trace');
    
    // Clear authentication data
    authService.logout();
    setUser(null);
    
    // Clear all user-specific data from localStorage
    clearAllUserData();
  };

  // Clear all user-specific data when user changes or logs out
  const clearAllUserData = () => {
    console.log('🧹 Clearing all user-specific data');
    
    // Clear game-specific data
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('kidsInvestmentGame_')) {
        console.log('🗑️ Removing user game data:', key);
        localStorage.removeItem(key);
      }
    });
    
    // Clear any other user-specific data
    localStorage.removeItem('kidsInvestmentGame'); // Legacy key
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    setUser,
    clearAllUserData,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};